import rp, { AppComponent, Saveable, ToolbarItemOptions } from "@reporting/rp-app-host";
import { customElement, property } from "lit/decorators.js";
import { css } from "lit";
import { ColorTypes, ComponentAdapter, actions, dom, utils } from "@omegagrid/core";
import { Editor } from "@omegagrid/editor";
import { GridSource, NameSource, initOptions } from "@omegagrid/grid";
import { Toolbar, ToolbarItem } from "@omegagrid/toolbar";
import { Resource } from "@reporting/rp-types/ext";
import { msg } from "@omegagrid/localize";

/** blank sheet for a new/empty resource - the shape of og-gridgroup's empty source */
const EMPTY_SHEET_SOURCE: GridSource = {
	options: {rows: 100, columns: 20, editable: true}
};

export type SheetEditorOptions = {
	readOnly?: boolean,
	toolbar?: {[key: string]: ToolbarItemOptions}
};

/** keys of value that differ (deep) from defaults */
const diffDeep = (value: Record<string, unknown>, defaults: Record<string, unknown>): Record<string, unknown> => {
	const diff: Record<string, unknown> = {};
	for (const key of Object.keys(value)) {
		const v = value[key], d = defaults?.[key];
		if (utils.isObject(v) && utils.isObject(d)) {
			const child = diffDeep(v as Record<string, unknown>, d as Record<string, unknown>);
			if (Object.keys(child).length) diff[key] = child;
		} else if (JSON.stringify(v) !== JSON.stringify(d)) {
			diff[key] = v;
		}
	}
	return diff;
};

const createDefaultOptions = (): SheetEditorOptions => ({
	toolbar: {
		save: {
			id: 'save',
			type: 'button',
			text: 'Save changes',
			color: ColorTypes.Green,
			icon: 'save',
			enabled: true,
			ord: 1
		},
		discard: {
			id: 'discard',
			type: 'button',
			text: 'Discard changes',
			color: ColorTypes.Red,
			icon: 'floppy-disk-circle-xmark',
			enabled: true,
			ord: 2
		}
	}
});

export abstract class SheetEditorAdapter extends ComponentAdapter<SheetEditor> {

	abstract getContent(): string|Promise<string>;

	abstract save(content: string): void|boolean|Promise<void|boolean>;

	abstract getResource(): Resource|Promise<Resource>;

	getOptions(): SheetEditorOptions|Promise<SheetEditorOptions> {
		return this.createOptions({});
	}

	createOptions(options?: Partial<SheetEditorOptions>): SheetEditorOptions {
		return utils.mergeDeep(createDefaultOptions(), options ?? {});
	}

	onAction(action: actions.Action) { }

}

@customElement('rp-editor-sheet')
export class SheetEditor extends AppComponent implements Saveable {

	static styles = [AppComponent.styles, css`
		:host {
			display: flex;
			flex-direction: column;
			height: 100%;
		}

		og-toolbar {
			flex: 0 0 auto;
		}

		og-editor {
			flex: 1 1 auto;
			min-height: 0;
			width: 100%;
		}
	`];

	@property({type: Object})
	adapter: SheetEditorAdapter;

	protected options: SheetEditorOptions;
	private _loading = false;
	private _editing = false;
	private _saving = false;
	private _pendingSave = false;

	/** created imperatively once and kept across lit renders - og-editor loses all state on recreation */
	private _editor: Editor;
	get editor() {
		if (this._editor) return this._editor;
		const editor = this._editor = dom.createElement<Editor>('og-editor', {
			listeners: [['grid.edit', this._onGridEdit]]
		});
		editor.enableDrag = false;
		return editor;
	}

	private _toolbar: Toolbar;
	get toolbar() {
		if (this._toolbar) return this._toolbar;
		return (this._toolbar = dom.createElement<Toolbar>('og-toolbar', {
			listeners: [['toolbar.action', this._onToolbarAction]]
		}));
	}

	connectedCallback(): void {
		super.connectedCallback();
		this.addEventListener('keydown', this._onKeyDown);
	}

	disconnectedCallback(): void {
		super.disconnectedCallback();
		this.removeEventListener('keydown', this._onKeyDown);
	}

	async updated(props: Map<string, unknown>) {
		if (props.has('adapter') && this.adapter) {
			this.options = await this.adapter.getOptions();
			this.toolbar.items = this.createToolbarItems();
			this.adapter.attach(this);
			await this.load();
		}
	}

	private createToolbarItems(): ToolbarItem[] {
		if (!this.options?.toolbar) return [];
		const items = Object.values(this.options.toolbar).filter(i => i.enabled !== false);
		items.sort((a, b) => (a.ord ?? 0) - (b.ord ?? 0));
		return items as ToolbarItem[];
	}

	private parseContent(content: string): GridSource {
		if (content) {
			try {
				const source = JSON.parse(content);
				if (source && typeof source == 'object') return source as GridSource;
			} catch (e) {
				rp.log.error(e);
				rp.host.showAlert({content: msg('Sheet definition is not a valid JSON, starting with a blank sheet.'), type: 'warning'});
			}
		}
		return utils.mergeDeep({}, EMPTY_SHEET_SOURCE);
	}

	async load() {
		if (this._loading) return;
		this._loading = true;
		try {
			const [content, resource] = await Promise.all([this.adapter.getContent(), this.adapter.getResource()]);
			const source = this.parseContent(content);
			const name = resource?.name?.split('/').pop() ?? 'sheet';
			this.editor.setSourceData({
				models: [{...source, name, index: 0}],
				layout: {items: [{size: null, tabs: [{id: name, title: name}]}]}
			});
			// og-editor is sized by the layout only after this update - relayout once sizes settle
			requestAnimationFrame(() => this.layout());
		} finally {
			this._loading = false;
		}
	}

	/** current editor state as the persisted content - a single GridSource JSON object */
	serialize(): string {
		const model = this._editor?.gridContainer?.getSourceData()?.models?.[0];
		if (!model) return null;
		// JSON round-trip drops function-valued and undefined fields
		const source = JSON.parse(JSON.stringify(model)) as Record<string, unknown>;
		return JSON.stringify(this.cleanSource(source), null, '\t');
	}

	/** strip runtime keys, resolved defaults and model-derived data so the file stays a minimal static template */
	private cleanSource(source: Record<string, unknown>): Record<string, unknown> {
		delete source.name;
		delete source.index;

		// model options come back fully resolved - keep only the keys that differ from the runtime defaults
		if (utils.isObject(source.options)) {
			const options = diffDeep(source.options as Record<string, unknown>, initOptions({}) as unknown as Record<string, unknown>);
			delete options.commandWindow; // forced by og-editor
			source.options = options;
		}

		// names auto-registered from row/column names are model-derived, keep only custom ranges
		if (Array.isArray(source.names)) {
			const derived = new Set([...(source.columns as {name?: string}[] ?? []), ...(source.rows as {name?: string}[] ?? [])]
				.map(item => item?.name).filter(Boolean));
			source.names = (source.names as NameSource[]).filter(item => !derived.has(item.name));
		}

		for (const key of Object.keys(source)) {
			const value = source[key];
			if (value == null
				|| (Array.isArray(value) && !value.length)
				|| (utils.isObject(value) && !Object.keys(value).length)) delete source[key];
		}
		return source;
	}

	async save() {
		if (this._saving) {
			this._pendingSave = true;
			return;
		}

		const content = this.serialize();
		if (content == null) return;

		this._saving = true;
		let result: void|boolean;
		try {
			result = await this.adapter.save(content);
		} finally {
			this._saving = false;
		}

		if (this._pendingSave) {
			this._pendingSave = false;
			this.save();
		}

		if (result !== false) {
			this._editing = false;
			this.dispatchEvent(new CustomEvent('save', {bubbles: true, composed: true}));
		}
		return result;
	}

	async discard() {
		if (!this._editing) return;
		await this.load();
		this._editing = false;
		this.dispatchEvent(new CustomEvent('save', {bubbles: true, composed: true}));
	}

	layout() {
		this._editor?.layout();
	}

	_onKeyDown = (e: KeyboardEvent) => {
		if (this.options?.toolbar?.save?.enabled && !this.options?.readOnly && e.key == 's' && e.ctrlKey) {
			e.preventDefault();
			this.save();
		}
	}

	_onGridEdit = () => {
		if (!this._editing) {
			this._editing = true;
			this.dispatchEvent(new CustomEvent('edit', {bubbles: true, composed: true}));
		}
	}

	_onToolbarAction = (e: actions.ActionEvent) => {
		// plain buttons dispatch 'change', dropdown-button and its menu dispatch 'select'
		if (e.action.type == 'change' || e.action.type == 'select') {
			if (e.action.key == 'save') {
				this.save();
			} else if (e.action.key == 'discard') {
				this.discard();
			}
		}

		this.adapter?.onAction(e.action);
	}

	render = () => [this.toolbar, this.editor];

}
